import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import { markRaw } from 'vue';
import EditorMedia from '../Components/EditorMedia.vue';
import { blockRhythm, blockFrame, blockVideo, blockFlush, blockCaption, embed, embed16x9, embedItem } from '../Themes/EditorClasses';

function convertVideoElement(domNode) {
    const src = domNode.getAttribute('src');

    if (!src) return null;

    return { node: $createVideoNode({ src }) };
}

export class VideoNode extends DecoratorNode {
    __src;
    __caption;
    __spacing;

    static getType() {
        return 'video';
    }

    static clone(node) {
        return new VideoNode(node.__src, node.__caption, node.__spacing, node.__key);
    }

    constructor(src, caption, spacing, key) {
        super(key);
        this.__src = src;
        this.__caption = caption || '';
        this.__spacing = spacing !== false;
    }

    static importJSON(serializedNode) {
        const { src, caption, spacing } = serializedNode;
        return $createVideoNode({ src, caption, spacing });
    }

    exportJSON() {
        return {
            type: 'video',
            version: 1,
            src: this.__src,
            caption: this.__caption,
            spacing: this.__spacing,
        };
    }

    static importDOM() {
        return {
            video: () => ({
                conversion: convertVideoElement,
                priority: 0,
            }),
        };
    }

    exportDOM() {
        const figure = document.createElement('figure');
        figure.className = `${blockRhythm} ${blockFrame} ${blockVideo}`;
        if (!this.__spacing) {
            figure.classList.add(blockFlush);
        }

        const embedWrapper = document.createElement('span');
        embedWrapper.className = `${embed} ${embed16x9}`;

        const video = document.createElement('video');
        video.setAttribute('src', this.__src);
        video.setAttribute('class', embedItem);
        video.setAttribute('controls', 'true');

        embedWrapper.appendChild(video);
        figure.appendChild(embedWrapper);

        if (this.__caption) {
            const figcaption = document.createElement('figcaption');
            figcaption.className = blockCaption;
            figcaption.textContent = this.__caption;
            figure.appendChild(figcaption);
        }

        return { element: figure };
    }

    isInline() {
        return false;
    }

    createDOM() {
        return document.createElement('div');
    }

    updateDOM(prevNode) {
        return prevNode.__src !== this.__src
            || prevNode.__caption !== this.__caption
            || prevNode.__spacing !== this.__spacing;
    }

    decorate(editor) {
        return {
            component: markRaw(EditorMedia),
            props: {
                type: 'video',
                src: this.__src,
                caption: this.__caption,
                spacing: this.__spacing,
                editable: editor.isEditable(),
            },
        };
    }

    getSrc() {
        return this.__src;
    }

    getCaption() {
        return this.__caption;
    }

    getSpacing() {
        return this.__spacing;
    }

    setSrc(src) {
        this.getWritable().__src = src;
    }

    setCaption(caption) {
        this.getWritable().__caption = caption || '';
    }

    setSpacing(spacing) {
        this.getWritable().__spacing = spacing !== false;
    }
}

export function $createVideoNode({ src, caption, spacing }) {
    return $applyNodeReplacement(new VideoNode(src, caption || '', spacing));
}

export function $isVideoNode(node) {
    return node instanceof VideoNode;
}
